import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SidebarContent } from "./Sidebar";
import { List, House, PenNib, FileText, Gavel } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useRBAC } from "@/contexts/RBACContext";

/**
 * Floating hamburger trigger (top-left) + Sheet drawer wrapping the full Sidebar.
 * Only rendered on < lg screens.
 */
export function MobileNavDrawer({ onOpenPalette }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  // Auto-close when the route changes (parent effect)
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="mobile-nav-trigger"
          aria-label="Open navigation"
          className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md glass-subtle border border-[var(--border)] text-[var(--text)] hover:border-[var(--accent)]"
        >
          <List size={18} />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="p-0 w-72 max-w-[85vw] bg-[var(--bg-2)]/95 backdrop-blur-2xl border-e-[var(--border)]" data-testid="mobile-nav-drawer">
        <SidebarContent onOpenPalette={() => { setOpen(false); onOpenPalette?.(); }} onNavigate={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

/**
 * Fixed bottom tab bar — the 4 highest-traffic routes, filtered by RBAC.
 * Only rendered on < lg screens.
 */
export function MobileBottomNav() {
  const { user } = useAuth();
  const { allowed, ready } = useRBAC();
  if (!user) return null;

  const all = [
    { to: "/app",            label: "Home",     icon: House,    testid: "mobile-tab-dashboard", module: "dashboard", end: true },
    { to: "/app/studio",     label: "Studio",   icon: PenNib,   testid: "mobile-tab-studio",    module: "studio" },
    { to: "/app/repository", label: "Contracts",icon: FileText, testid: "mobile-tab-repository",module: "repository" },
    { to: "/app/workflows",  label: "Flows",    icon: Gavel,    testid: "mobile-tab-workflows", module: "workflows" },
  ];
  const tabs = ready ? all.filter((t) => !t.module || allowed.has(t.module)) : all;
  if (tabs.length === 0) return null;

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur-2xl"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className={`grid grid-cols-${tabs.length} h-14`}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            data-testid={tab.testid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] transition ${
                isActive ? "text-[var(--accent)]" : "text-[var(--text-2)] hover:text-[var(--text)]"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <tab.icon size={18} weight={isActive ? "fill" : "regular"} />
                <span>{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
